import { DateTimePicker, FormInput, getDate } from '@systemeio/ui'
import { UnprocessableContentError } from '@systemeio/util'
import {
  PublicApiSettingsCreateInterface,
  PublicApiSettingsCreatedInterface,
} from 'modules/profile/public-api-settings/types/public-api-settings-interface'
import React, { useState } from 'react'
import { CreateDataType } from 'shared/components/create-modal'
import { CreateDataInterface } from 'shared/components/listing-layout'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import {
  PublicApiSettingsCreateErrorInterface,
  PublicApiSettingsErrorInterface,
} from '../types/public-api-settings-error-interface'
import { useCreatePublicApiKey } from './use-create-public-api-keys'
import { usePublicApiKeys } from './use-public-api-keys'

const defaultValues = {
  name: '',
  expiresAt: '',
}

const defaultErrors = {
  name: '',
  expiresAt: '',
}

interface useCreateDataPublicApiKeysProps {
  onCreate: (data: PublicApiSettingsCreatedInterface) => Promise<void>
}

export const useCreateDataPublicApiKeys = ({ onCreate }: useCreateDataPublicApiKeysProps) => {
  const { t } = useLocoTranslation()

  const [errors, setErrors] = useState<PublicApiSettingsCreateErrorInterface>(defaultErrors)

  const { user, intlLocale } = useUser()

  const { publicApiKeys } = usePublicApiKeys()
  const { createPublicApiKey } = useCreatePublicApiKey()

  const onCreateModalOpen = async () => setErrors(defaultErrors)

  const onCreateKey = async ({
    name,
    expiresAt,
  }: CreateDataType<PublicApiSettingsCreateInterface>) => {
    try {
      setErrors(defaultErrors)
      const data = await createPublicApiKey({ name, expiresAt })

      await onCreate(data)

      return data
    } catch (e) {
      if (e instanceof UnprocessableContentError) {
        const errors = (e as any).errors as PublicApiSettingsErrorInterface
        errors.violations.forEach(
          ({ propertyPath, message }) =>
            Object.keys(defaultErrors).includes(propertyPath) &&
            setErrors(prev => ({ ...prev, [propertyPath]: message })),
        )
      }
      throw e
    }
  }

  return {
    createData: {
      disabled: !publicApiKeys || publicApiKeys?.length >= 3,
      caption: t('dashboard.user_settings.public_api.title'),
      requiredFields: ['name'],
      onOpen: onCreateModalOpen,
      onCreate: async data => {
        await onCreateKey(data)
      },
      defaultValues: defaultValues,
      creatable: [
        {
          field: 'name',
          onRender: (value, onChange) => (
            <FormInput
              required
              key="key-name"
              label={t('dashboard.user_settings.public_api.name')}
              value={value}
              onChange={e => onChange(e.target.value)}
              maxLength={256}
              error={errors.name}
            />
          ),
        },
        {
          field: 'expiresAt',
          onRender: (value, onChange) => (
            <DateTimePicker
              label={t('dashboard.user_settings.public_api.expires_at')}
              key={'expiresAt'}
              value={value || null}
              onChange={date => {
                onChange(date || undefined)
              }}
              customProps={{
                minDate: getDate(
                  new Date(new Date().setHours(new Date().getHours() + 24 * 31)).toISOString(),
                  user?.timezone,
                ),
              }}
              error={errors.expiresAt}
              createdAt={user?.createdAt}
              timezone={user?.timezone}
              intlLocale={intlLocale}
            />
          ),
        },
      ],
    } as CreateDataInterface<PublicApiSettingsCreateInterface>,
  }
}
